// Pure helpers for the Concepts tab proficiency rendering. Kept out of the
// component so they can be unit-tested directly.

import type { ConceptsOverview } from "@/interfaces/concepts.interface";

// Exact proficiency colours from the Figma design tokens.
export const PROFICIENCY_COLORS = {
  red: "#D74249", // utility-error-500
  orange: "#FF9A42", // utility-warning-300
  lightGreen: "#5DDA94", // utility-success-300
  darkGreen: "#2EC772", // utility-success-400
};

// The concept level is a universal 0–3 scale for EVERY assessment type, where
// level 3 is always "Expected" (the top of the band). Only the GRADE that each
// level corresponds to differs per type — mirrors `conceptMasteryToGrade()` in
// reflective-learning-dashboard-api/src/utils/pdf/pdf.js.
export const TOP_LEVEL = 3;

const GRADE_BY_LEVEL: Record<string, number[]> = {
  MATHS_JUNIOR_PRIMARY: [0, 1, 2, 3],
  MATHS_SENIOR_PRIMARY: [0, 1, 3, 6],
  MATHS_SENIOR_PRIMARY_V2: [0, 1, 3, 6],
  ENGLISH_SENIOR_PRIMARY: [0, 1, 3, 6],
};
// Junior High, Matric Foundations, Adult Numeracy, English Junior High, ...
const DEFAULT_GRADE_BY_LEVEL = [0, 3, 6, 9];

export function conceptMasteryToGrade(
  assessmentType: string | null | undefined,
  level: number
): number {
  const map = (assessmentType && GRADE_BY_LEVEL[assessmentType]) || DEFAULT_GRADE_BY_LEVEL;
  return map[level] ?? 0;
}

export interface ProficiencySegment {
  level: number;
  color: string;
  label: string;
}

/**
 * Build the 4-segment config (colour + grade label per level) for a thread bar.
 * Labels come from the assessment type's level→grade mapping; level 3 is always
 * "Expected". For Senior-Primary the level-1 (orange) bucket is rarely populated
 * — it just renders empty, which is why SP "looks" like three colours.
 */
export function buildSegments(
  assessmentType: string | null | undefined
): ProficiencySegment[] {
  const gradeX = conceptMasteryToGrade(assessmentType, 1);
  const gradeY = conceptMasteryToGrade(assessmentType, 2);
  return [
    { level: 0, color: PROFICIENCY_COLORS.red, label: `Not yet at Grade ${gradeX} level` },
    { level: 1, color: PROFICIENCY_COLORS.orange, label: `At Grade ${gradeX} level` },
    { level: 2, color: PROFICIENCY_COLORS.lightGreen, label: `At Grade ${gradeY} level` },
    { level: 3, color: PROFICIENCY_COLORS.darkGreen, label: "At Expected level" },
  ];
}

export interface BaselineIncompleteFigure {
  total: number;
  notCompleted: number;
  percentage: number;
}

/**
 * Students in the filter who have NOT completed the baseline for a specific
 * thread. `total` stays the whole filtered cohort; a student counts as
 * "completed" only if they have a non-null baseline level for that thread, so
 * students with no submission at all are not-completed for every thread.
 */
export function computeThreadBaselineIncomplete(
  overview: ConceptsOverview | null,
  threadId: number | null,
  total: number
): BaselineIncompleteFigure {
  total = total || 0;
  if (!overview || threadId == null) {
    return { total, notCompleted: total, percentage: total ? 100 : 0 };
  }
  let withBaseline = 0;
  for (const group of overview.byAssessmentType || []) {
    for (const row of group.masteryTable || []) {
      const done = (row.concepts || []).some(
        (c) => c.threadId === threadId && c.baselineLevel !== null
      );
      if (done) withBaseline++;
    }
  }
  const notCompleted = Math.max(0, total - withBaseline);
  return { total, notCompleted, percentage: total ? (notCompleted / total) * 100 : 0 };
}
